import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import type { IssueSeverity, IssueStatus } from '@chatsdet/types/issues';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

type BulkArchiveFilters = {
  severity?: IssueSeverity;
  propertyId?: string;
  status?: IssueStatus;
  search?: string;
  lastSeenStart?: string;
  lastSeenEnd?: string;
  categoryIds?: string[];
  includeArchived?: boolean;
};

type BulkArchiveIssuesPayload =
  | {
      issueIds: string[];
    }
  | {
      selectAll: true;
      filters: BulkArchiveFilters;
      excludeIssueIds?: string[];
    };

type BulkArchiveIssuesResult = {
  updatedCount: number;
};

export function useBulkArchiveIssues(
  options?: UseMutationOptions<BulkArchiveIssuesResult, Error, BulkArchiveIssuesPayload>
) {
  return useMutation<BulkArchiveIssuesResult, Error, BulkArchiveIssuesPayload>({
    mutationFn: async (payload) => {
      const response = await fetch(`${API_URL}/v2/issues/bulk-archive`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw await toApiError(response, 'Failed to archive issues');
      }

      const parsed = await response.json();

      if (!parsed?.ok) {
        throw await toApiError(response, 'Failed to archive issues');
      }

      return {
        updatedCount: Number(parsed.updatedCount ?? 0),
      };
    },
    ...options,
  });
}
